import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ReportCard } from '@components/Pages/AnalyticsPage/ReportCard';
import { GroupedSelect } from '@components/ui/GroupedSelect/GroupedSelect';
import { fetchSiteProfessions } from '@apis/professions';
import {
  buildProfessionLabel,
  professionsToGroupedOptions,
} from '../utils/professionFormatting';
import { CardIcon } from '../CardIcon';

export interface IContractorAnalysisSubmission {
  professionId: string;
  professionLabel: string;
}

interface IContractorAnalysisCardProps {
  isSelected: boolean;
  onSelect: (name: 'contractorAnalysis') => void;
  onSubmit: (payload: IContractorAnalysisSubmission) => void;
}

export function ContractorAnalysisCard({ isSelected, onSelect, onSubmit }: IContractorAnalysisCardProps) {
  const { t } = useTranslation();
  const { siteId = '' } = useParams<{ siteId: string }>();
  const [professionId, setProfessionId] = useState<string | null>(null);

  // Live working-professions for the project. The BFF proxies trusstor's
  // `getWorkingProfessions` and the response is sorted server-side, so we
  // map directly into the `GroupedSelect` shape without re-sorting here.
  // `staleTime: 5min` matches the working-set's cadence — additions /
  // edits land via the trusstor backoffice and aren't latency-sensitive.
  const professionsQuery = useQuery({
    queryKey: ['site-professions', siteId] as const,
    queryFn: ({ signal }) => fetchSiteProfessions(siteId, signal),
    enabled: !!siteId,
    staleTime: 5 * 60_000,
  });

  const groups = useMemo(
    () => professionsToGroupedOptions(professionsQuery.data ?? []),
    [professionsQuery.data],
  );

  // Look up the picked option to thread the full label back through the
  // submission — the parent persists `professionLabel` so the recent rail
  // can render it without a second lookup.
  const pickedProfession = useMemo(() => {
    if (!professionId || !professionsQuery.data) return null;
    return professionsQuery.data.find((p) => p.id === professionId) ?? null;
  }, [professionId, professionsQuery.data]);

  const handleSubmit = () => {
    if (!professionId || !pickedProfession) return;
    onSubmit({
      professionId,
      professionLabel: buildProfessionLabel(pickedProfession),
    });
  };

  const isLoading = professionsQuery.isLoading;
  const placeholder = isLoading
    ? t('analytics.filters.loadingPlaceholder')
    : t('analytics.filters.selectPlaceholder');

  return (
    <ReportCard
      name="contractorAnalysis"
      title={t('analytics.cards.contractorAnalysis.title')}
      description={t('analytics.cards.contractorAnalysis.description')}
      icon={<CardIcon name="helmet" />}
      isSelected={isSelected}
      onSelect={() => onSelect('contractorAnalysis')}
      onSubmit={handleSubmit}
      submitLabel={t('analytics.createButton')}
      submitDisabled={professionId === null || isLoading}
    >
      <GroupedSelect
        value={professionId}
        onChange={setProfessionId}
        groups={groups}
        placeholder={placeholder}
        ariaLabel={t('analytics.filters.profession')}
        disabled={isLoading || groups.length === 0}
      />
    </ReportCard>
  );
}
